import { FC, useEffect, useRef, useState } from 'react'
import { useIntl } from 'react-intl'
import { wikiContentStyle } from './WikiContent.style'
import VideoEmbed from './videoembed/VideoEmbed'
import ReactMarkdown from 'react-markdown'
import { WikiService } from 'src/dataSources/wiki/Wiki'
import FullSizeDialog from './fullsizedialog/FullSizeDialog'

/*
* Enum describing the allowed types of a WikiContent object
*/
export enum WikiContentType {
  TEXT = 'TEXT',
  IMAGE = 'IMAGE',
  VIDEO = 'VIDEO'
}

/**
 * Object type describing a Wiki Content
 */
export interface WikiContent {
  key?: string
  type: WikiContentType
  text: string
  url?: string
}

/**
 * WikiContentComponentProps contains properties for the component.
 */
export interface WikiContentComponentProps {
  content: WikiContent
}

/**
 * WikiContentComponent displays a content object from a WikiSection
 * @param props Properties for component.
 * @returns JSX component.
 */
export const WikiContentComponent: FC<WikiContentComponentProps> = (props: WikiContentComponentProps) => {
  const classes = wikiContentStyle()
  const intl = useIntl()

  const wikiService = new WikiService()
  const rootRef = useRef<HTMLDivElement>(null)

  const [rootWidth, setRootWidth] = useState<number>(0)
  const [fullSizeKey, setFullSizeKey] = useState<string|undefined>(undefined)

  useEffect(() => {
    const width = rootRef.current?.offsetWidth === undefined ? 0 : rootRef.current?.offsetWidth
    setRootWidth(width)
    // eslint-disable-next-line
  }, [rootRef])

  return (
    <div className={classes.root} ref={rootRef}>
      {props.content.type === WikiContentType.TEXT && (
        <ReactMarkdown linkTarget='_blank' className={classes.text}>
          {props.content.text}
        </ReactMarkdown>
      )}
      {props.content.type === WikiContentType.IMAGE &&
      props.content.key !== undefined &&
      rootWidth > 10 && (
        <>
          <img
            src={wikiService.getImageUrl(props.content.key, rootWidth)}
            alt={intl.formatMessage({ id: 'wiki.content.image' })}
            className={classes.image}
            onClick={() => setFullSizeKey(props.content.key)}
          />
          <ReactMarkdown linkTarget='_blank' className={classes.subtitle}>{props.content.text}</ReactMarkdown>
        </>
      )}
      {props.content.type === WikiContentType.VIDEO && props.content.url !== undefined && (
        <>
          <VideoEmbed url={props.content.url} />
          <ReactMarkdown linkTarget='_blank' className={classes.subtitle}>{props.content.text}</ReactMarkdown>
        </>
      )}
      <FullSizeDialog
        open={fullSizeKey !== undefined}
        imageKey={fullSizeKey ?? ''}
        handleClose={() => setFullSizeKey(undefined)}
      />
    </div>
  )
}

export default WikiContentComponent
